import type { FloorView, PodSummary } from '@/api/rest';
import { useDashboardStore } from '@/state/store';
import { Leaderboard } from '@/components/Leaderboard';
import { useBreakpoint } from '@/hooks/useBreakpoint';

const FLOOR_BACKGROUND: Record<string, string> = {
  canary_lab: 'linear-gradient(135deg, #1f3a5f, #21262d)',
  active_offices: 'linear-gradient(135deg, #1f6feb22, #161b22)',
  reduced_floor: 'linear-gradient(135deg, #d2992233, #161b22)',
  frozen_basement: 'linear-gradient(135deg, #58a6ff33, #0d1117)',
};

const RISK_COLOR: Record<PodSummary['risk_state'], string> = {
  active: '#2ea043',
  watch: '#d29922',
  reduced: '#db6d28',
  frozen: '#58a6ff',
};

/**
 * Phase 0 placeholder: render the building as a stack of floors,
 * each floor a grid of pod boxes.  Click handler is a stub; later
 * phases swap this whole component for a Pixi canvas backed by the
 * same store selectors.
 */
export function BuildingOverview() {
  const building = useDashboardStore((s) => s.building);
  const bp = useBreakpoint();
  if (!building) {
    return (
      <section
        style={{
          padding: 24,
          color: '#8b949e',
        }}
      >
        Loading building snapshot…
      </section>
    );
  }
  const isMobile = bp === 'mobile';
  const isTablet = bp === 'tablet';
  return (
    <section
      style={{
        padding: isMobile ? 12 : isTablet ? 16 : 24,
        overflowY: 'auto',
        display: 'grid',
        gridTemplateColumns: isMobile
          ? 'minmax(0, 1fr)'
          : isTablet
            ? 'minmax(0, 1fr) 220px'
            : 'minmax(0, 1fr) 260px',
        gap: 16,
        minHeight: 0,
        alignContent: 'start',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {building.floors.map((floor) => (
          <FloorRow key={floor.id} floor={floor} />
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          position: isMobile ? 'static' : 'sticky',
          top: 0,
          order: isMobile ? -1 : 0,
        }}
      >
        <Leaderboard />
      </div>
    </section>
  );
}

function FloorRow({ floor }: { floor: FloorView }) {
  return (
    <article
      style={{
        background: FLOOR_BACKGROUND[floor.id] ?? '#161b22',
        border: '1px solid #21262d',
        borderRadius: 12,
        padding: 16,
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 14, color: '#e6edf3' }}>{floor.label}</h2>
        <span style={{ fontSize: 12, color: '#8b949e' }}>{floor.pods.length} pod(s)</span>
      </header>
      {floor.pods.length === 0 ? (
        <div
          style={{
            padding: 18,
            border: '1px dashed #30363d',
            borderRadius: 8,
            color: '#6e7681',
            textAlign: 'center',
            fontSize: 12,
          }}
        >
          empty floor
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))',
            gap: 12,
          }}
        >
          {floor.pods.map((pod) => (
            <PodCard key={pod.pod_id} pod={pod} />
          ))}
        </div>
      )}
    </article>
  );
}

function PodCard({ pod }: { pod: PodSummary }) {
  const accent = RISK_COLOR[pod.risk_state] ?? '#8b949e';
  const selectPod = useDashboardStore((s) => s.selectPod);
  return (
    <button
      type="button"
      onClick={() => selectPod(pod.pod_id)}
      style={{
        background: '#0d1117',
        borderRadius: 8,
        padding: 12,
        border: `1px solid ${accent}33`,
        position: 'relative',
        overflow: 'hidden',
        textAlign: 'left',
        cursor: 'pointer',
        color: 'inherit',
        font: 'inherit',
        transition: 'transform .15s ease, border-color .15s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.borderColor = accent;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.borderColor = `${accent}33`;
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          background: accent,
        }}
      />
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 6,
        }}
      >
        <strong style={{ fontSize: 13 }}>{pod.name}</strong>
        {pod.is_canary && (
          <span
            style={{
              fontSize: 10,
              padding: '2px 6px',
              borderRadius: 4,
              background: '#1f6feb',
              color: 'white',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            canary
          </span>
        )}
      </header>
      <div style={{ fontSize: 11, color: '#8b949e' }}>{pod.pod_id}</div>
      <div
        style={{
          marginTop: 8,
          fontSize: 11,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ color: accent }}>{pod.risk_state}</span>
        <span style={{ color: '#8b949e' }}>
          {pod.haircut_pct > 0 ? `haircut ${(pod.haircut_pct * 100).toFixed(0)}%` : 'no haircut'}
        </span>
      </div>
      <div style={{ marginTop: 6, fontSize: 11, color: '#8b949e' }}>
        capital ₽{Math.round(pod.capital).toLocaleString()}
      </div>
    </button>
  );
}
